<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getRandomColor } from 'dashboard/helper/labelColor';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { accountId } = useAccount();
const store = useStore();

// MakerHub first, end to end. Smith3D is fully configurable INCLUDING its master
// ENABLE toggle (owner unlocked 2026-07-08). enabled_s3 defaults OFF and the owner
// keeps it disabled for now — S3 arming still has no live consumer (mk-patrol
// excludes the S3 inboxes 62-65; dispatch unbuilt), so the toggle is config-only.
const COMPANIES = [
  { code: 'mk', locked: false },
  { code: 's3', locked: false },
];
const RELABEL_MODES = ['shadow', 'add_only', 'full'];
const SEND_ALL_CLEAR = ['first', 'always', 'never'];
const MAX_PROMPT = 500;

const isLoading = ref(true);
const isSaving = ref(false);
const activeCompany = ref('mk');

// --- global config (single cs:patrol:config keys, shared across companies) ---
const testMode = ref(true);
const relabelMode = ref('shadow');
const stepinBreaksQuiet = ref(true);
const summaryTimes = ref('09:00,14:00,17:00');
const boardTimes = ref('');
const workStart = ref('09:00');
const workEnd = ref('18:00');
const workDays = ref([1, 2, 3, 4, 5, 6]);
const sendAllClear = ref('first');
const humanLabelTtlH = ref('24');
const labelFlapH = ref('12');
const relabelMinConf = ref('0.8');
const realtimeBatchMin = ref('5');
const realtimeMinGapMin = ref('15');
const realtimeDailyCap = ref('8');
const pingQuietWindows = ref('10:30-11:00,14:00-15:00');
const maxItemsPerSection = ref('5');

// --- per-company config (suffixed keys + maps) -------------------------------
const enabled = ref({ mk: false, s3: false });
const teamRecipients = ref({ mk: [], s3: [] }); // [{ name, to }]
const contacts = ref({ mk: [], s3: [] }); // [{ name, whatsapp, labels, due_h, escalate_to }]
const labelKinds = ref({ mk: {}, s3: {} }); // { label: { kind, clear_on_resolve } }
const agentMap = ref({ mk: {}, s3: {} }); // { uid: { active, realtime, labels, prompt } }
const coverage = ref({});
const lastRun = ref(null); // most recent patrol tick summary (cs:patrol:last_run)

// WhatsApp numbers are the single source on users.custom_attributes — edited
// here OR in Agents settings; saved through the agents API, not cs:patrol.
const waNumbers = ref({});
const waOriginal = ref({});

// new-item drafts
const newRecipient = ref({
  mk: { name: '', to: '' },
  s3: { name: '', to: '' },
});

const agents = useMapGetter('agents/getAgents');
const labels = useMapGetter('labels/getLabels');
const teams = useMapGetter('teams/getTeams');
const teamMembersFor = useMapGetter('teamMembers/getTeamMembers');

// Static-keyed option lists (avoid dynamic i18n keys).
const companyTabs = computed(() => [
  { code: 'mk', label: t('AI_PATROL.COMPANY.MAKERHUB'), locked: false },
  { code: 's3', label: t('AI_PATROL.COMPANY.SMITH3D'), locked: false },
]);
const relabelModeOptions = computed(() => [
  { value: 'shadow', label: t('AI_PATROL.MASTER.RELABEL_MODE.SHADOW') },
  { value: 'add_only', label: t('AI_PATROL.MASTER.RELABEL_MODE.ADD_ONLY') },
  { value: 'full', label: t('AI_PATROL.MASTER.RELABEL_MODE.FULL') },
]);
const sendAllClearOptions = computed(() => [
  { value: 'first', label: t('AI_PATROL.GLOBAL.ALL_CLEAR.FIRST') },
  { value: 'always', label: t('AI_PATROL.GLOBAL.ALL_CLEAR.ALWAYS') },
  { value: 'never', label: t('AI_PATROL.GLOBAL.ALL_CLEAR.NEVER') },
]);
const kindOptions = computed(() => [
  { value: 'domain', label: t('AI_PATROL.TAXONOMY.KIND.DOMAIN') },
  { value: 'status', label: t('AI_PATROL.TAXONOMY.KIND.STATUS') },
]);
const weekDayOptions = computed(() => [
  { value: 1, label: t('AI_PATROL.GLOBAL.DAY.MON') },
  { value: 2, label: t('AI_PATROL.GLOBAL.DAY.TUE') },
  { value: 3, label: t('AI_PATROL.GLOBAL.DAY.WED') },
  { value: 4, label: t('AI_PATROL.GLOBAL.DAY.THU') },
  { value: 5, label: t('AI_PATROL.GLOBAL.DAY.FRI') },
  { value: 6, label: t('AI_PATROL.GLOBAL.DAY.SAT') },
  { value: 7, label: t('AI_PATROL.GLOBAL.DAY.SUN') },
]);

const co = computed(() => activeCompany.value);
const activeLocked = computed(
  () => COMPANIES.find(c => c.code === co.value)?.locked
);
const activeCoverage = computed(() => coverage.value?.[co.value] || {});

// The last patrol tick as report lines (each rendered in its own row, so the
// strings live in JS — no bare template text for the i18n linter, and no
// whitespace-sensitive element for prettier to fight).
const lastRunLines = computed(() => {
  const r = lastRun.value;
  if (!r) return [];
  const n = key => Number(r[key] || 0);
  const inUrgent = n('reurgent') + n('buried_surfaced');
  const outUrgent =
    n('closed_det') + n('closed_llm') + n('closed_bcast') + n('notif_spam');
  const opus =
    n('opus') + n('relabel_opus') + n('pending_opus') + n('buried_opus');
  return [
    `Ran ${r.ran_myt || '?'} MYT · relabel: ${r.relabel_mode || '?'}`,
    `Urgent — queue ${n('urgent')} · +${inUrgent} in · -${outUrgent} out · escalated ${n('escalated')} · kept ${n('kept')} · skipped ${n('skipped')}`,
    `Pending — seen ${n('pending_seen')} · classified ${n('pending_classified')}`,
    `Labels — scanned ${n('relabel_seen')} · judged ${n('relabel_judged')} · written ${n('relabel_wrote')}`,
    `Opus calls ${opus} · errors ${n('errors')}`,
  ];
});

// Company membership = TEAM membership (§7: derive people-company from team_id).
// Each company tab shows only agents in its team — makerhub agents on MakerHub,
// smith3d agents on Smith3D. Match the team by name (the same key the sidebar
// company views use); an agent in both teams shows on both tabs.
const TEAM_NAME_BY_CODE = { mk: 'makerhub', s3: 'smith3d' };
const teamIdFor = code =>
  (teams.value || []).find(
    team => (team.name || '').toLowerCase() === TEAM_NAME_BY_CODE[code]
  )?.id;
const companyAgents = computed(() => {
  const teamId = teamIdFor(co.value);
  if (!teamId) return [];
  const memberIds = new Set(
    (teamMembersFor.value(teamId) || []).map(m => m.id)
  );
  return (agents.value || []).filter(a => memberIds.has(a.id));
});

// Labels are company-scoped by their native team_id (§7: team_id is canonical).
// Each tab shows only its team's labels.
const companyLabels = computed(() => {
  const teamId = teamIdFor(co.value);
  if (!teamId) return [];
  return (labels.value || []).filter(label => label.team_id === teamId);
});
// The backend coverage list is not team-scoped; keep only labels that belong to
// this tab's company (req 1) so an S3 label never shows as a MakerHub gap.
const companyLabelTitles = computed(
  () => new Set(companyLabels.value.map(label => label.title))
);
const unassignedLabels = computed(() =>
  (activeCoverage.value.unassigned_labels || []).filter(title =>
    companyLabelTitles.value.has(title)
  )
);

const teamsLink = computed(() => ({
  name: 'settings_teams_list',
  params: { accountId: accountId.value },
}));
const labelsLink = computed(() => ({
  name: 'labels_list',
  params: { accountId: accountId.value },
}));

// --- helpers -----------------------------------------------------------------
const toList = csv =>
  (csv || '')
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);

const parseJson = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const entryFor = uid => {
  const map = agentMap.value[co.value];
  if (!map[uid])
    map[uid] = { active: false, realtime: false, labels: [], prompt: '' };
  return map[uid];
};

const toggleAgentLabel = (uid, label) => {
  const entry = entryFor(uid);
  entry.labels = entry.labels.includes(label)
    ? entry.labels.filter(l => l !== label)
    : [...entry.labels, label];
};

const toggleWorkDay = day => {
  workDays.value = workDays.value.includes(day)
    ? workDays.value.filter(d => d !== day)
    : [...workDays.value, day].sort((a, b) => a - b);
};

const labelKind = label => labelKinds.value[co.value][label]?.kind || 'domain';
const setLabelKind = (label, kind) => {
  labelKinds.value[co.value][label] = {
    ...(labelKinds.value[co.value][label] || {}),
    kind,
  };
};
const clearOnResolve = label =>
  Boolean(labelKinds.value[co.value][label]?.clear_on_resolve);
const setClearOnResolve = (label, value) => {
  labelKinds.value[co.value][label] = {
    kind: labelKind(label),
    ...(labelKinds.value[co.value][label] || {}),
    clear_on_resolve: value,
  };
};

// Company patrol label selection (§1): the enabled labels ARE the keys of
// labels_<code> — the taxonomy the relabel sweep reads (mk-patrol patrol.py).
// Toggling a chip adds/drops the key; enabling defaults the label to 'domain'.
// With >=1 enabled, patrol tags fitting conversations while it categorises the
// Urgent/Pending queue. The per-label kind/clear detail below stays for the
// backend lifecycle rules but only surfaces for labels that are enabled here.
const isPatrolLabel = title => Boolean(labelKinds.value[co.value][title]);
const toggleCompanyLabel = title => {
  const map = labelKinds.value[co.value];
  if (map[title]) delete map[title];
  else map[title] = { kind: 'domain' };
};
const enabledCompanyLabels = computed(() =>
  companyLabels.value.filter(label => isPatrolLabel(label.title))
);

const addRecipient = () => {
  const draft = newRecipient.value[co.value];
  if (!draft.to.trim()) return;
  teamRecipients.value[co.value].push({
    name: draft.name.trim(),
    to: draft.to.trim(),
  });
  newRecipient.value[co.value] = { name: '', to: '' };
};
const removeRecipient = index => {
  teamRecipients.value[co.value].splice(index, 1);
};

const addContact = () => {
  contacts.value[co.value].push({
    name: '',
    whatsapp: '',
    labels: [],
    due_h: 24,
    escalate_to: '',
  });
};
const removeContact = index => {
  contacts.value[co.value].splice(index, 1);
};

// --- inline label add / remove (native labels API, scoped to the tab's team) --
const newLabelTitle = ref({ mk: '', s3: '' });
const isAddingLabel = ref(false);
const labelToDelete = ref(null);
const showDeleteLabel = ref(false);
const deleteLabelMessage = computed(() =>
  labelToDelete.value ? ` ${labelToDelete.value.title}?` : ''
);

const addLabel = async () => {
  const teamId = teamIdFor(co.value);
  const title = newLabelTitle.value[co.value].trim().toLowerCase();
  if (!teamId || title.length < 2 || title.includes(' ')) return;
  isAddingLabel.value = true;
  try {
    await store.dispatch('labels/create', {
      title,
      color: getRandomColor(),
      show_on_sidebar: true,
      team_id: teamId,
    });
    newLabelTitle.value[co.value] = '';
  } catch (error) {
    useAlert(error?.message || t('AI_PATROL.TAXONOMY.ADD_ERROR'));
  } finally {
    isAddingLabel.value = false;
  }
};

const askRemoveLabel = label => {
  labelToDelete.value = label;
  showDeleteLabel.value = true;
};
const closeRemoveLabel = () => {
  showDeleteLabel.value = false;
  labelToDelete.value = null;
};
const removeLabel = async () => {
  const label = labelToDelete.value;
  closeRemoveLabel();
  if (!label) return;
  try {
    await store.dispatch('labels/delete', label.id);
  } catch (error) {
    useAlert(error?.message || t('AI_PATROL.TAXONOMY.DELETE_ERROR'));
  }
};

// --- hydrate / persist -------------------------------------------------------
const hydrateGlobal = config => {
  testMode.value = String(config.test_mode ?? '1') !== '0';
  relabelMode.value = RELABEL_MODES.includes(config.relabel_mode)
    ? config.relabel_mode
    : 'shadow';
  stepinBreaksQuiet.value = String(config.stepin_breaks_quiet ?? '1') !== '0';
  summaryTimes.value = config.summary_times || '09:00,14:00,17:00';
  boardTimes.value = config.board_times || '';
  workStart.value = config.work_start || '09:00';
  workEnd.value = config.work_end || '18:00';
  const days = toList(config.work_days)
    .map(Number)
    .filter(d => d >= 1 && d <= 7);
  if (days.length) workDays.value = days;
  sendAllClear.value = SEND_ALL_CLEAR.includes(config.send_all_clear)
    ? config.send_all_clear
    : 'first';
  humanLabelTtlH.value = config.human_label_ttl_h || '24';
  labelFlapH.value = config.label_flap_h || '12';
  relabelMinConf.value = config.relabel_min_conf || '0.8';
  realtimeBatchMin.value = config.realtime_batch_min || '5';
  realtimeMinGapMin.value = config.realtime_min_gap_min || '15';
  realtimeDailyCap.value = config.realtime_daily_cap || '8';
  pingQuietWindows.value =
    config.ping_quiet_windows ?? '10:30-11:00,14:00-15:00';
  maxItemsPerSection.value = config.max_items_per_section || '5';
};

const hydrateCompany = (config, code) => {
  enabled.value[code] = String(config[`enabled_${code}`] ?? '0') === '1';
  teamRecipients.value[code] = parseJson(config[`team_${code}`], []);
  contacts.value[code] = parseJson(config[`contacts_${code}`], []);
  labelKinds.value[code] = parseJson(config[`labels_${code}`], {});
};

const hydrateMap = (rawMap, code) => {
  const map = {};
  Object.entries(rawMap || {}).forEach(([uid, json]) => {
    const entry = parseJson(json, {});
    map[uid] = {
      active: entry.active === true,
      realtime: entry.realtime === true,
      labels: Array.isArray(entry.labels) ? entry.labels : [],
      prompt: entry.prompt || '',
    };
  });
  agentMap.value[code] = map;
};

const hydrateWaNumbers = () => {
  const next = {};
  (agents.value || []).forEach(agent => {
    next[agent.id] = agent.custom_attributes?.whatsapp_number || '';
  });
  waNumbers.value = next;
  waOriginal.value = { ...next };
};

const hydrate = data => {
  const config = data.config || {};
  hydrateGlobal(config);
  hydrateCompany(config, 'mk');
  hydrateCompany(config, 's3');
  hydrateMap(data.map_mk, 'mk');
  hydrateMap(data.map_s3, 's3');
  coverage.value = data.coverage || {};
  lastRun.value = data.last_run || null;
  hydrateWaNumbers();
};

const buildMap = code => {
  const out = {};
  const names = Object.fromEntries(
    (agents.value || []).map(a => [String(a.id), a.name])
  );
  Object.entries(agentMap.value[code]).forEach(([uid, entry]) => {
    if (!entry.active && !entry.labels.length && !entry.prompt) return;
    out[uid] = {
      labels: entry.labels,
      realtime: entry.realtime,
      active: entry.active,
      prompt: entry.prompt || null,
      summary_times: null,
      name: names[uid] || '',
    };
  });
  return out;
};

const buildPayload = () => ({
  test_mode: testMode.value ? '1' : '0',
  relabel_mode: relabelMode.value,
  stepin_breaks_quiet: stepinBreaksQuiet.value ? '1' : '0',
  summary_times: summaryTimes.value,
  board_times: boardTimes.value,
  work_start: workStart.value,
  work_end: workEnd.value,
  work_days: [...workDays.value].sort((a, b) => a - b).join(','),
  send_all_clear: sendAllClear.value,
  human_label_ttl_h: String(humanLabelTtlH.value),
  label_flap_h: String(labelFlapH.value),
  relabel_min_conf: String(relabelMinConf.value),
  realtime_batch_min: String(realtimeBatchMin.value),
  realtime_min_gap_min: String(realtimeMinGapMin.value),
  realtime_daily_cap: String(realtimeDailyCap.value),
  ping_quiet_windows: pingQuietWindows.value,
  max_items_per_section: String(maxItemsPerSection.value),
  enabled_mk: enabled.value.mk ? '1' : '0',
  enabled_s3: enabled.value.s3 ? '1' : '0',
  team_mk: JSON.stringify(teamRecipients.value.mk),
  team_s3: JSON.stringify(teamRecipients.value.s3),
  contacts_mk: JSON.stringify(contacts.value.mk),
  contacts_s3: JSON.stringify(contacts.value.s3),
  labels_mk: JSON.stringify(labelKinds.value.mk),
  labels_s3: JSON.stringify(labelKinds.value.s3),
  map_mk: JSON.stringify(buildMap('mk')),
  map_s3: JSON.stringify(buildMap('s3')),
});

const baseUrl = computed(() => `/api/v1/accounts/${accountId.value}/mh_patrol`);

// WhatsApp numbers are single-source on the agent — persist any changed ones
// through the agents API (deep merge preserves other custom_attributes).
const saveWaNumbers = async () => {
  const changed = Object.entries(waNumbers.value).filter(
    ([uid, number]) => number !== waOriginal.value[uid]
  );
  await Promise.all(
    changed.map(([uid, number]) =>
      store.dispatch('agents/update', {
        id: Number(uid),
        custom_attributes: { whatsapp_number: number },
      })
    )
  );
};

const loadConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await window.axios.get(baseUrl.value);
    hydrate(data);
  } catch (error) {
    useAlert(t('AI_PATROL.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    await saveWaNumbers();
    const { data } = await window.axios.put(baseUrl.value, buildPayload());
    hydrate(data);
    useAlert(t('AI_PATROL.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AI_PATROL.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  await store.dispatch('teams/get');
  Object.keys(TEAM_NAME_BY_CODE).forEach(code => {
    const teamId = teamIdFor(code);
    if (teamId) store.dispatch('teamMembers/get', { teamId });
  });
  loadConfig();
});
</script>

<template>
  <div class="flex flex-col w-full max-w-3xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader
      :title="$t('AI_PATROL.TITLE')"
      :description="$t('AI_PATROL.DESCRIPTION')"
    />

    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <woot-loading-state v-if="isLoading" />

      <form v-else @submit.prevent="save">
        <!-- Company tabs — segmented control, bigger + solid fill when selected -->
        <div class="flex gap-2 rounded-xl bg-n-alpha-2 p-1.5" role="tablist">
          <button
            v-for="company in companyTabs"
            :key="company.code"
            type="button"
            role="tab"
            :aria-selected="activeCompany === company.code"
            class="flex-1 rounded-lg px-5 py-3 text-base font-semibold transition-all"
            :class="
              activeCompany === company.code
                ? 'bg-n-teal-9 text-white shadow-md ring-1 ring-n-teal-9'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            @click="activeCompany = company.code"
          >
            {{ company.label }}
            <span
              v-if="company.locked"
              class="ltr:ml-1 rtl:mr-1 text-xs opacity-80"
            >
              {{ $t('AI_PATROL.COMPANY.LOCKED') }}
            </span>
          </button>
        </div>

        <!-- Section: last patrol run (read-only status, global) -->
        <SectionLayout
          :title="$t('AI_PATROL.LAST_RUN.TITLE')"
          :description="$t('AI_PATROL.LAST_RUN.NOTE')"
          class="!pt-4"
        >
          <div
            v-if="lastRunLines.length"
            class="grid gap-1 overflow-x-auto rounded-lg border border-n-weak bg-n-alpha-1 p-3 font-mono text-xs leading-relaxed text-n-slate-11"
          >
            <span v-for="(line, i) in lastRunLines" :key="i">{{ line }}</span>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            {{ $t('AI_PATROL.LAST_RUN.EMPTY') }}
          </p>
        </SectionLayout>

        <!-- Section: company master + staged arming -->
        <SectionLayout
          :title="$t('AI_PATROL.MASTER.TITLE')"
          :description="$t('AI_PATROL.MASTER.NOTE')"
          class="!pt-4"
        >
          <div class="grid gap-4">
            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors"
              :class="[
                activeLocked
                  ? 'border-n-weak opacity-60 cursor-not-allowed'
                  : 'cursor-pointer',
                enabled[activeCompany] && !activeLocked
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak',
              ]"
            >
              <input
                v-model="enabled[activeCompany]"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
                :disabled="activeLocked"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_PATROL.MASTER.ENABLE_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{
                    activeLocked
                      ? $t('AI_PATROL.MASTER.LOCKED_HELP')
                      : $t('AI_PATROL.MASTER.ENABLE_HELP')
                  }}
                </span>
              </span>
            </label>

            <fieldset class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_PATROL.MASTER.RELABEL_LABEL') }}
              </legend>
              <label
                v-for="option in relabelModeOptions"
                :key="option.value"
                class="flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors"
                :class="
                  relabelMode === option.value
                    ? 'border-n-teal-8 bg-n-teal-2'
                    : 'border-n-weak hover:bg-n-alpha-1'
                "
              >
                <input
                  v-model="relabelMode"
                  type="radio"
                  :value="option.value"
                  class="accent-n-teal-9"
                />
                <span class="text-sm text-n-slate-12">{{ option.label }}</span>
              </label>
            </fieldset>
          </div>
        </SectionLayout>

        <!-- Section: company patrol labels (§1 — labels patrol may apply) -->
        <SectionLayout
          :title="$t('AI_PATROL.TAXONOMY.TITLE')"
          :description="$t('AI_PATROL.TAXONOMY.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <div v-if="companyLabels.length" class="flex flex-wrap gap-1.5">
              <button
                v-for="label in companyLabels"
                :key="label.id"
                type="button"
                class="rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                :class="
                  isPatrolLabel(label.title)
                    ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="toggleCompanyLabel(label.title)"
              >
                {{ label.title }}
              </button>
            </div>
            <p v-else class="text-xs text-n-slate-10">
              {{ $t('AI_PATROL.TAXONOMY.EMPTY') }}
            </p>

            <div class="flex items-center gap-2">
              <input
                v-model="newLabelTitle[activeCompany]"
                type="text"
                :placeholder="$t('AI_PATROL.TAXONOMY.ADD_PLACEHOLDER')"
                class="!mb-0 flex-grow text-sm lowercase"
                @keydown.enter.prevent="addLabel"
              />
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-1 disabled:opacity-50"
                :disabled="isAddingLabel"
                @click="addLabel"
              >
                {{ $t('AI_PATROL.TAXONOMY.ADD') }}
              </button>
            </div>

            <!-- per-label lifecycle detail, only for the labels enabled above -->
            <div v-if="enabledCompanyLabels.length" class="grid gap-2">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('AI_PATROL.TAXONOMY.ADVANCED') }}
              </span>
              <div
                v-for="label in enabledCompanyLabels"
                :key="label.id"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak px-3 py-2"
              >
                <span class="text-sm text-n-slate-12">{{ label.title }}</span>
                <div class="flex items-center gap-3">
                  <div class="flex gap-1">
                    <button
                      v-for="kind in kindOptions"
                      :key="kind.value"
                      type="button"
                      class="rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                      :class="
                        labelKind(label.title) === kind.value
                          ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                          : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                      "
                      @click="setLabelKind(label.title, kind.value)"
                    >
                      {{ kind.label }}
                    </button>
                  </div>
                  <label class="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      :checked="clearOnResolve(label.title)"
                      class="accent-n-teal-9"
                      @change="
                        setClearOnResolve(label.title, $event.target.checked)
                      "
                    />
                    <span class="text-xs text-n-slate-11">
                      {{ $t('AI_PATROL.TAXONOMY.CLEAR_ON_RESOLVE') }}
                    </span>
                  </label>
                  <button
                    type="button"
                    class="text-base leading-none text-n-slate-10 hover:text-n-ruby-11"
                    :aria-label="$t('AI_PATROL.TAXONOMY.REMOVE')"
                    @click="askRemoveLabel(label)"
                  >
                    {{ '✕' }}
                  </button>
                </div>
              </div>
            </div>

            <router-link :to="labelsLink" class="text-xs text-n-teal-11">
              {{ $t('AI_PATROL.TAXONOMY.MANAGE') }}
            </router-link>
          </div>
        </SectionLayout>

        <!-- Section: coverage strip -->
        <SectionLayout
          :title="$t('AI_PATROL.COVERAGE.TITLE')"
          :description="$t('AI_PATROL.COVERAGE.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <div class="grid gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('AI_PATROL.COVERAGE.UNASSIGNED') }}
              </span>
              <div
                v-if="unassignedLabels.length"
                class="flex flex-wrap gap-1.5"
              >
                <span
                  v-for="label in unassignedLabels"
                  :key="label"
                  class="rounded-full bg-n-amber-3 px-2.5 py-0.5 text-xs text-n-amber-11"
                >
                  {{ label }}
                </span>
              </div>
              <span v-else class="text-xs text-n-slate-10">
                {{ $t('AI_PATROL.COVERAGE.ALL_ASSIGNED') }}
              </span>
            </div>
            <div class="grid gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('AI_PATROL.COVERAGE.MISSING_NUMBER') }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ activeCoverage.agents_missing_number?.length || 0 }}
              </span>
            </div>
          </div>
        </SectionLayout>

        <!-- Section: per-agent cards -->
        <SectionLayout
          :title="$t('AI_PATROL.AGENTS.TITLE')"
          :description="$t('AI_PATROL.AGENTS.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <div
              v-for="agent in companyAgents"
              :key="agent.id"
              class="grid gap-3 rounded-lg border border-n-weak p-3"
            >
              <div class="flex items-center justify-between gap-3">
                <span class="text-sm font-medium text-n-slate-12 capitalize">
                  {{ agent.name }}
                </span>
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    v-model="entryFor(agent.id).active"
                    type="checkbox"
                    class="accent-n-teal-9"
                  />
                  <span class="text-xs text-n-slate-11">
                    {{ $t('AI_PATROL.AGENTS.ACTIVE') }}
                  </span>
                </label>
              </div>

              <div class="grid gap-3 sm:grid-cols-2">
                <label class="grid gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ $t('AI_PATROL.AGENTS.WHATSAPP') }}
                  </span>
                  <input
                    v-model="waNumbers[agent.id]"
                    type="text"
                    :placeholder="$t('AI_PATROL.AGENTS.WHATSAPP_PLACEHOLDER')"
                    class="!mb-0 text-sm"
                  />
                </label>
                <label class="flex items-end gap-2 pb-1">
                  <input
                    v-model="entryFor(agent.id).realtime"
                    type="checkbox"
                    class="accent-n-teal-9"
                  />
                  <span class="text-xs text-n-slate-11">
                    {{ $t('AI_PATROL.AGENTS.REALTIME') }}
                  </span>
                </label>
              </div>

              <div class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.AGENTS.LABELS') }}
                </span>
                <div v-if="companyLabels.length" class="flex flex-wrap gap-1.5">
                  <button
                    v-for="label in companyLabels"
                    :key="label.id"
                    type="button"
                    class="rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                    :class="
                      entryFor(agent.id).labels.includes(label.title)
                        ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                        : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                    "
                    @click="toggleAgentLabel(agent.id, label.title)"
                  >
                    {{ label.title }}
                  </button>
                </div>
                <span v-else class="text-xs text-n-slate-10">
                  {{ $t('AI_PATROL.AGENTS.NO_LABELS') }}
                </span>
              </div>

              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.AGENTS.STYLE') }}
                </span>
                <textarea
                  v-model="entryFor(agent.id).prompt"
                  rows="2"
                  :maxlength="MAX_PROMPT"
                  :placeholder="$t('AI_PATROL.AGENTS.STYLE_PLACEHOLDER')"
                  class="!mb-0 text-sm"
                />
                <span
                  class="text-xs text-n-slate-10 ltr:text-right rtl:text-left"
                >
                  {{
                    $t('AI_PATROL.AGENTS.CHAR_COUNT', {
                      count: (entryFor(agent.id).prompt || '').length,
                      max: MAX_PROMPT,
                    })
                  }}
                </span>
              </label>
            </div>
            <p v-if="!companyAgents.length" class="text-xs text-n-slate-10">
              {{ $t('AI_PATROL.AGENTS.EMPTY') }}
            </p>
          </div>
        </SectionLayout>

        <!-- Section: team board recipients -->
        <SectionLayout
          :title="$t('AI_PATROL.TEAM.TITLE')"
          :description="$t('AI_PATROL.TEAM.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <div
              v-for="(recipient, index) in teamRecipients[activeCompany]"
              :key="index"
              class="flex items-center gap-2"
            >
              <span
                class="inline-flex flex-grow items-center gap-2 rounded-lg bg-n-slate-3 px-3 py-1.5 text-sm text-n-slate-12"
              >
                <span class="font-medium">{{ recipient.name || '—' }}</span>
                <span class="text-n-slate-10">{{ recipient.to }}</span>
              </span>
              <button
                type="button"
                class="text-base leading-none text-n-slate-10 hover:text-n-ruby-11"
                :aria-label="$t('AI_PATROL.TEAM.REMOVE')"
                @click="removeRecipient(index)"
              >
                {{ '\u2715' }}
              </button>
            </div>

            <div class="flex flex-wrap items-end gap-2">
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.TEAM.NAME') }}
                </span>
                <input
                  v-model="newRecipient[activeCompany].name"
                  type="text"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1 flex-grow">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.TEAM.TO') }}
                </span>
                <input
                  v-model="newRecipient[activeCompany].to"
                  type="text"
                  :placeholder="$t('AI_PATROL.TEAM.TO_PLACEHOLDER')"
                  class="!mb-0 text-sm"
                  @keydown.enter.prevent="addRecipient"
                />
              </label>
              <button
                type="button"
                class="rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-1"
                @click="addRecipient"
              >
                {{ $t('AI_PATROL.TEAM.ADD') }}
              </button>
            </div>
            <router-link :to="teamsLink" class="text-xs text-n-teal-11">
              {{ $t('AI_PATROL.TEAM.MANAGE') }}
            </router-link>
          </div>
        </SectionLayout>

        <!-- Section: contact-owner cards -->
        <SectionLayout
          :title="$t('AI_PATROL.CONTACTS.TITLE')"
          :description="$t('AI_PATROL.CONTACTS.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <div
              v-for="(contact, index) in contacts[activeCompany]"
              :key="index"
              class="grid gap-3 rounded-lg border border-n-weak p-3 sm:grid-cols-2"
            >
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.CONTACTS.NAME') }}
                </span>
                <input
                  v-model="contact.name"
                  type="text"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.CONTACTS.WHATSAPP') }}
                </span>
                <input
                  v-model="contact.whatsapp"
                  type="text"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1 sm:col-span-2">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.CONTACTS.LABELS') }}
                </span>
                <div v-if="companyLabels.length" class="flex flex-wrap gap-1.5">
                  <button
                    v-for="label in companyLabels"
                    :key="label.id"
                    type="button"
                    class="rounded-full border px-2.5 py-0.5 text-xs transition-colors"
                    :class="
                      contact.labels.includes(label.title)
                        ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                        : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                    "
                    @click="
                      contact.labels = contact.labels.includes(label.title)
                        ? contact.labels.filter(l => l !== label.title)
                        : [...contact.labels, label.title]
                    "
                  >
                    {{ label.title }}
                  </button>
                </div>
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.CONTACTS.DUE_H') }}
                </span>
                <input
                  v-model.number="contact.due_h"
                  type="number"
                  min="1"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.CONTACTS.ESCALATE_TO') }}
                </span>
                <div class="flex items-center gap-2">
                  <input
                    v-model="contact.escalate_to"
                    type="text"
                    class="!mb-0 flex-grow text-sm"
                  />
                  <button
                    type="button"
                    class="text-base leading-none text-n-slate-10 hover:text-n-ruby-11"
                    :aria-label="$t('AI_PATROL.CONTACTS.REMOVE')"
                    @click="removeContact(index)"
                  >
                    {{ '\u2715' }}
                  </button>
                </div>
              </label>
            </div>
            <button
              type="button"
              class="justify-self-start rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-1"
              @click="addContact"
            >
              {{ $t('AI_PATROL.CONTACTS.ADD') }}
            </button>
          </div>
        </SectionLayout>

        <!-- Section: global dispatch config -->
        <SectionLayout
          :title="$t('AI_PATROL.GLOBAL.TITLE')"
          :description="$t('AI_PATROL.GLOBAL.NOTE')"
          with-border
        >
          <div class="grid gap-4">
            <fieldset class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_PATROL.GLOBAL.WORK_DAYS') }}
              </legend>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="day in weekDayOptions"
                  :key="day.value"
                  type="button"
                  class="rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
                  :class="
                    workDays.includes(day.value)
                      ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="toggleWorkDay(day.value)"
                >
                  {{ day.label }}
                </button>
              </div>
            </fieldset>

            <div class="grid gap-3 sm:grid-cols-2">
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.WORK_START') }}
                </span>
                <input v-model="workStart" type="time" class="!mb-0 text-sm" />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.WORK_END') }}
                </span>
                <input v-model="workEnd" type="time" class="!mb-0 text-sm" />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.SUMMARY_TIMES') }}
                </span>
                <input
                  v-model="summaryTimes"
                  type="text"
                  :placeholder="
                    $t('AI_PATROL.GLOBAL.SUMMARY_TIMES_PLACEHOLDER')
                  "
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.BOARD_TIMES') }}
                </span>
                <input
                  v-model="boardTimes"
                  type="text"
                  :placeholder="$t('AI_PATROL.GLOBAL.BOARD_TIMES_PLACEHOLDER')"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.QUIET_WINDOWS') }}
                </span>
                <input
                  v-model="pingQuietWindows"
                  type="text"
                  :placeholder="
                    $t('AI_PATROL.GLOBAL.QUIET_WINDOWS_PLACEHOLDER')
                  "
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.SEND_ALL_CLEAR') }}
                </span>
                <select v-model="sendAllClear" class="!mb-0 text-sm">
                  <option
                    v-for="mode in sendAllClearOptions"
                    :key="mode.value"
                    :value="mode.value"
                  >
                    {{ mode.label }}
                  </option>
                </select>
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.TTL') }}
                </span>
                <input
                  v-model.number="humanLabelTtlH"
                  type="number"
                  min="0"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.FLAP') }}
                </span>
                <input
                  v-model.number="labelFlapH"
                  type="number"
                  min="0"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.MIN_CONF') }}
                </span>
                <input
                  v-model="relabelMinConf"
                  type="text"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.BATCH_MIN') }}
                </span>
                <input
                  v-model.number="realtimeBatchMin"
                  type="number"
                  min="1"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.MIN_GAP') }}
                </span>
                <input
                  v-model.number="realtimeMinGapMin"
                  type="number"
                  min="0"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.DAILY_CAP') }}
                </span>
                <input
                  v-model.number="realtimeDailyCap"
                  type="number"
                  min="0"
                  class="!mb-0 text-sm"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_PATROL.GLOBAL.MAX_ITEMS') }}
                </span>
                <input
                  v-model.number="maxItemsPerSection"
                  type="number"
                  min="1"
                  class="!mb-0 text-sm"
                />
              </label>
            </div>

            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="stepinBreaksQuiet"
                type="checkbox"
                class="accent-n-teal-9"
              />
              <span class="text-sm text-n-slate-12">
                {{ $t('AI_PATROL.GLOBAL.STEPIN_BREAKS_QUIET') }}
              </span>
            </label>
          </div>
        </SectionLayout>

        <div class="pt-2">
          <NextButton blue :is-loading="isSaving" type="submit">
            {{ $t('AI_PATROL.SAVE') }}
          </NextButton>
        </div>
      </form>
    </div>

    <woot-delete-modal
      v-model:show="showDeleteLabel"
      :on-close="closeRemoveLabel"
      :on-confirm="removeLabel"
      :title="$t('AI_PATROL.TAXONOMY.DELETE_TITLE')"
      :message="$t('AI_PATROL.TAXONOMY.DELETE_MESSAGE')"
      :message-value="deleteLabelMessage"
      :confirm-text="$t('AI_PATROL.TAXONOMY.DELETE_YES')"
      :reject-text="$t('AI_PATROL.TAXONOMY.DELETE_NO')"
    />
  </div>
</template>
